import { Gender, StorySlug } from "@/api/episodes-types";
import { MasterCombination } from "@/api/master-combinations-types";
import { Prompt, PromptType } from "@/api/prompts-types";
import { StoryGenerationRequest } from "@/api/stories-types";
import { StoryConfig } from "@/api/story-configs-types";
import { RoleName } from "@/api/users-types";
import { CSVUpload } from "@/components/story-generation/CSVUpload";
import { StoryFormItem } from "@/components/story-generation/StoryFormItem";
import {
  ModelOption,
  StoryFormItemErrors,
  StoryFormItemValues,
} from "@/components/story-generation/types";
import { NoPermissions } from "@/components/ui/NoPermissions";
import { AlertSnackbar, ApiErrorSnackbar } from "@/components/ui/Snackbar";
import { useHasRole } from "@/hooks/use-has-role";
import { useAllMasterCombinations } from "@/queries/master-combinations/use-master-combinations";
import { useAllPrompts } from "@/queries/prompts/use-prompts";
import { useGenerateStories } from "@/queries/stories/use-generate-stories";
import { useStoryConfigs } from "@/queries/story-configs/use-story-configs";
import { storyCategories, storySubcategories } from "@/utils/story-categories";
import AddIcon from "@mui/icons-material/Add";
import { Button, Stack, Typography } from "@mui/material";
import { useMemo, useState } from "react";

const emptyErrorData: StoryFormItemErrors = {
  storyType: "",
  category: "",
  subcategory: "",
  gender: "",
  numberOfPages: "",
  pageSize: "",
  count: "",
  promptName: "",
  promptRevision: "",
  modelName: "",
  imagePromptName: "",
  imagePromptRevision: "",
  configRevision: "",
  useMasterCombination: "",
  masterCombinationName: "",
};

export function StoryGenerationPage() {
  const [formItems, setFormItems] = useState<StoryFormItemValues[]>([]);
  const [formItemErrors, setFormItemErrors] = useState<StoryFormItemErrors[]>(
    [],
  );

  const { data: prompts } = useAllPrompts();
  const {
    mutate: generateStories,
    error,
    isSuccess,
  } = useGenerateStories({
    onSuccess: () => setFormItems([]),
  });
  const { data: configs } = useStoryConfigs();
  const { data: combinations } = useAllMasterCombinations();

  const promptsByStoryType = useMemo(() => {
    const acc: Record<string, Prompt[]> = {};
    prompts?.items.forEach((p) => {
      if (!acc[p.storyType]) {
        acc[p.storyType] = [];
      }
      acc[p.storyType].push(p);
    });
    return acc;
  }, [prompts]);

  function csvToFormItems(rows: Record<string, string>[]) {
    const formItems: StoryFormItemValues[] = rows.map((item) => {
      const selectedPrompt = promptsByStoryType[item.storyType]?.find(
        (p) => p.name === item.promptName && p.type === PromptType.text,
      );
      const selectedPromptRevision = selectedPrompt?.revisions.find(
        (r) => r.revision === Number(item.promptRevision),
      );
      const selectedImagePrompt = promptsByStoryType[item.storyType]?.find(
        (p) => p.name === item.imagePromptName && p.type === PromptType.image,
      );
      const selectedImagePromptRevision = selectedImagePrompt?.revisions.find(
        (r) => r.revision === Number(item.imagePromptRevision),
      );
      const selectedConfig = configs?.items
        .find((c) => c.storyType === item.storyType)
        ?.revisions.find((r) => r.revision === Number(item.configRevision));
      return {
        storyType: Object.values(StorySlug).includes(
          item.storyType as StorySlug,
        )
          ? (item.storyType as StorySlug)
          : undefined,
        category: storyCategories.includes(item.category)
          ? item.category
          : undefined,
        subcategory: storySubcategories[
          item.category as keyof typeof storySubcategories
        ]?.includes(item.subcategory)
          ? item.subcategory
          : undefined,
        gender:
          item.gender === Gender.boy || item.gender === Gender.girl
            ? item.gender
            : undefined,
        numberOfPages: Number.isInteger(Number(item.numberOfPages))
          ? Number(item.numberOfPages)
          : 12,
        pageSize:
          item.pageSize && /^\d+-\d+$/.test(item.pageSize) // accepts only ranges like "45-55"
            ? item.pageSize
            : "45-55",
        count:
          item.count && Number.isInteger(Number(item.count))
            ? Number(item.count)
            : 1,
        promptName: selectedPrompt ? selectedPrompt.name : undefined,
        promptRevision: selectedPromptRevision
          ? selectedPromptRevision.revision
          : undefined,
        modelName:
          item.modelName &&
          Object.values(ModelOption).includes(item.modelName as ModelOption)
            ? item.modelName
            : undefined,
        imagePromptName: selectedImagePrompt
          ? selectedImagePrompt.name
          : undefined,
        imagePromptRevision: selectedImagePromptRevision
          ? selectedImagePromptRevision.revision
          : undefined,
        configRevision: selectedConfig ? selectedConfig.revision : undefined,
        useMasterCombination: false,
        masterCombinationName: undefined,
      };
    });
    setFormItems(formItems);
    validateFormItems(formItems); // setState is async, so we have to pass the new data directly here
  }

  function onFormItemChange(
    index: number,
    field: keyof StoryFormItemValues,
    value: string | number | Gender | boolean | undefined,
  ) {
    if (!formItems) {
      return;
    }
    formItems[index] = {
      ...formItems[index],
      [field]: value,
    };

    if (field === "category") {
      formItems[index].subcategory = undefined;
    }
    if (field === "storyType") {
      formItems[index].promptName = undefined;
      formItems[index].promptRevision = undefined;
      formItems[index].imagePromptName = undefined;
      formItems[index].imagePromptRevision = undefined;
      formItems[index].configRevision = undefined;
      formItems[index].masterCombinationName = undefined;
    }
    if (field === "promptName") {
      formItems[index].promptRevision = undefined;
    }
    if (field === "imagePromptName") {
      formItems[index].imagePromptRevision = undefined;
    }

    if (field === "useMasterCombination") {
      if (value) {
        formItems[index].promptName = undefined;
        formItems[index].promptRevision = undefined;
        formItems[index].imagePromptName = undefined;
        formItems[index].imagePromptRevision = undefined;
        formItems[index].configRevision = undefined;
        formItems[index].modelName = undefined;
      } else {
        formItems[index].masterCombinationName = undefined;
      }
    }

    if (field === "masterCombinationName") {
      const modelName = combinations?.items.find(
        (c) => c.name === value,
      )!.llmModel;
      formItems[index].modelName = modelName;
    }

    setFormItems([...formItems]);

    if (formItemErrors[index]?.[field]) {
      setFormItemErrors({
        ...formItemErrors,
        [index]: {
          ...formItemErrors[index],
          [field]: "",
        },
      });
    }

    if (field === "useMasterCombination") {
      if (value) {
        setFormItemErrors({
          ...formItemErrors,
          [index]: {
            ...formItemErrors[index],
            promptName: "",
            promptRevision: "",
            imagePromptName: "",
            imagePromptRevision: "",
            configRevision: "",
          },
        });
      } else {
        setFormItemErrors({
          ...formItemErrors,
          [index]: {
            ...formItemErrors[index],
            masterCombinationName: "",
          },
        });
      }
    }
  }

  function onRemoveFormItem(index: number) {
    if (!formItems) {
      return;
    }
    const updatedFormData = formItems.filter((_, i) => i !== index);
    setFormItems(updatedFormData);
    const updatedErrors = formItemErrors.filter((_, i) => i !== index);
    setFormItemErrors(updatedErrors);
  }

  function onAddFormItem() {
    if (!formItems) {
      return;
    }
    const newStory: StoryFormItemValues = {
      storyType: undefined,
      category: undefined,
      subcategory: undefined,
      gender: undefined,
      numberOfPages: 12,
      pageSize: "45-55",
      count: 1,
      promptName: undefined,
      promptRevision: undefined,
      modelName: undefined,
      imagePromptName: undefined,
      imagePromptRevision: undefined,
      configRevision: undefined,
      useMasterCombination: false,
      masterCombinationName: undefined,
    };
    setFormItems([...formItems, newStory]);
  }

  function validateFormItems(items?: StoryFormItemValues[]): boolean {
    items = items ?? formItems;
    if (!items) {
      return false;
    }
    const errors: StoryFormItemErrors[] = [];
    let wasError = false;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      errors[i] = { ...emptyErrorData };
      if (!item.storyType) {
        errors[i].storyType = "Please select a story type.";
        wasError = true;
      }
      if (!item.category) {
        errors[i].category = "Please select a category.";
        wasError = true;
      }
      if (!item.subcategory) {
        errors[i].subcategory = "Please select a subcategory.";
        wasError = true;
      }
      if (!item.gender) {
        errors[i].gender = "Please select a gender.";
        wasError = true;
      }
      if (!item.numberOfPages || item.numberOfPages <= 0) {
        errors[i].numberOfPages = "Please enter a valid number of pages.";
        wasError = true;
      }
      if (!item.pageSize || !/^\d+-\d+$/.test(item.pageSize)) {
        errors[i].pageSize =
          "Please enter a valid page size in the format min-max (e.g., 45-55).";
        wasError = true;
      }
      const [minSize, maxSize] = item.pageSize?.split("-").map(Number) ?? [
        0, 0,
      ];
      if (minSize <= 0 || maxSize <= 0 || minSize >= maxSize) {
        errors[i].pageSize =
          "Please ensure page size ranges are valid. Min should be less than max and both should be greater than 0.";
        wasError = true;
      }
      if (!item.count || item.count <= 0) {
        errors[i].count = "Please enter a valid count.";
        wasError = true;
      }
      if (item.useMasterCombination) {
        if (!item.masterCombinationName) {
          errors[i].masterCombinationName =
            "Please select a master combination by its name.";
          wasError = true;
        }
      } else {
        if (!item.promptName && item.storyType) {
          errors[i].promptName = "Please select prompt by its name.";
          wasError = true;
        }
        if (!item.promptRevision && item.promptName && item.storyType) {
          errors[i].promptRevision = "Please select prompt revision.";
          wasError = true;
        }
        if (item.imagePromptName && item.imagePromptRevision === undefined) {
          errors[i].imagePromptRevision =
            "Please select image prompt revision.";
          wasError = true;
        }
        if (item.imagePromptRevision !== undefined && !item.imagePromptName) {
          errors[i].imagePromptName = "Please select image prompt by its name.";
          wasError = true;
        }
      }
    }

    if (wasError) {
      setFormItemErrors(errors);
    } else {
      setFormItemErrors([]);
    }

    return !wasError;
  }

  function onGenerateStories() {
    if (!validateFormItems()) {
      return;
    }
    const generationRequests: StoryGenerationRequest[] = formItems.map((item) =>
      fromStoryFormItemValues(
        item,
        promptsByStoryType,
        configs?.items || [],
        combinations?.items || [],
      ),
    );
    generateStories({ items: generationRequests });
  }

  const canView = useHasRole(RoleName.episodeManager);

  if (!canView) {
    return <NoPermissions />;
  }

  if (formItems.length === 0) {
    return (
      <Stack gap={2} mb={2}>
        <AlertSnackbar severity="success">
          {isSuccess ? "Stories generated successfully!" : undefined}
        </AlertSnackbar>

        <Typography variant="h6">Upload CSV or Add Story Manually</Typography>
        <CSVUpload onDataParsed={csvToFormItems} />
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          size={"large"}
          onClick={onAddFormItem}
          sx={{ textTransform: "none", alignSelf: "end" }}
        >
          Add story manually
        </Button>
      </Stack>
    );
  }

  return (
    <Stack gap={2} mb={2}>
      <ApiErrorSnackbar error={error} />

      <Typography variant="h6">
        Verify and Edit Stories Before Generation
      </Typography>

      {formItems.map((item, index) => (
        <StoryFormItem
          key={index}
          index={index}
          values={item}
          errors={formItemErrors[index]}
          promptsByStoryType={promptsByStoryType}
          onRemove={onRemoveFormItem}
          onChange={onFormItemChange}
        />
      ))}

      <Stack direction="row" gap={1} justifyContent="flex-end">
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          size="large"
          onClick={onAddFormItem}
          sx={{ textTransform: "none" }}
        >
          Add story
        </Button>
        {formItems.length > 0 && (
          <Button
            variant="contained"
            size="large"
            sx={{ textTransform: "none" }}
            onClick={onGenerateStories}
          >
            Generate stories
          </Button>
        )}
      </Stack>
    </Stack>
  );
}

function fromStoryFormItemValues(
  item: StoryFormItemValues,
  prompts: Record<string, Prompt[]>,
  configs: StoryConfig[],
  combinations: MasterCombination[],
): StoryGenerationRequest {
  const shared = {
    storyType: item.storyType!,
    category: item.category!,
    subcategory: item.subcategory!,
    gender: item.gender!,
    numberOfPages: item.numberOfPages!,
    pageSize: item.pageSize,
    count: item.count,
    modelName: item.modelName,
  };

  if (item.useMasterCombination) {
    return {
      ...shared,
      masterCombinationId: combinations.find(
        (c) => c.name === item.masterCombinationName,
      )!.id,
    };
  } else {
    return {
      ...shared,
      promptId: prompts[item.storyType!]
        .find((p) => p.name === item.promptName && p.type === PromptType.text)!
        .revisions.find((r) => r.revision === item.promptRevision)!.id,
      imagePromptId:
        item.imagePromptName && item.imagePromptRevision
          ? prompts[item.storyType!]
              .find(
                (p) =>
                  p.name === item.imagePromptName &&
                  p.type === PromptType.image,
              )!
              .revisions.find((r) => r.revision === item.imagePromptRevision)!
              .id
          : undefined,
      configId: item.configRevision
        ? configs
            .find((c) => c.storyType === item.storyType)
            ?.revisions.find((r) => r.revision === item.configRevision)?.id
        : undefined,
    };
  }
}
